import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ClipboardText,
  Phone,
  Envelope,
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretDown,
  ListChecks,
  Clock,
  Hourglass,
  WarningCircle,
  CheckSquareOffset,
  CircleNotch,
  UserCircle,
  FunnelSimple,
  X,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { CalendarBlank, CalendarPlus, ArrowSquareOut, ArrowsClockwise, XCircle, PaperPlaneTilt } from '@phosphor-icons/react'
import { LinkedInBrandIcon } from '@/components/icons/LinkedInBrandIcon'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { tasksApi, meetingsApi, type Meeting } from '@/lib/api'
import BookMeetingModal from '@/pages/contacts/components/BookMeetingModal'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  TASK_TYPE_OPTIONS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  meetingProviderLabel,
  statusOptionsFor,
  taskPriorityLabel,
  taskStatusLabel,
  taskTypeLabel,
} from './taskLabels'
import {
  applyTaskListPatch,
  buildTaskListQuery,
  clearTaskListFilters,
  DEFAULT_SORT_DIR,
  hasActiveTaskFilters,
  parseTaskListParams,
  type SortDir,
  type TaskListParams,
  type TaskSortField,
} from './taskFilterParams'

// Meeting provider tag — understated neutral chip with a brand-colored status dot,
// matching the app's subtle table chrome (rather than a loud saturated pill).
// Colour only: the provider's *wording* comes from `meetingProviderLabel`, so a
// provider we haven't styled yet still reads as prose instead of a raw enum.
const PROVIDER_DOT: Record<string, string> = {
  outlook:  'bg-[#0f6cbd]',
  zoom:     'bg-[#2d8cff]',
  calendly: 'bg-orange-500',
}

// Priority chip — border + soft fill pair, dual-mode (§31 idiom)
const PRIORITY_CLS: Record<string, string> = {
  low:     'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  medium:  'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  high:    'bg-orange-500/10 text-orange-700 border-orange-500/25 dark:bg-orange-500/[0.12] dark:text-orange-400 dark:border-orange-500/25',
  urgent:  'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}

function getTypeIcon(taskType: string): PhosphorIcon {
  if (taskType.startsWith('linkedin_')) return LinkedInBrandIcon
  if (taskType === 'call') return Phone
  if (taskType === 'manual_email') return Envelope
  if (taskType === 'book_meeting' || taskType === 'meeting') return CalendarBlank
  return ClipboardText
}

// Overdue matches the backend stats definition (due timestamp in the past);
// callers additionally gate on task status so completed/cancelled rows never
// count as overdue.
function relativeDate(dateStr: string | null): { text: string; overdue: boolean } {
  if (!dateStr) return { text: '—', overdue: false }
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))
  const overdue = diffMs < 0
  if (diffDays === 0) return { text: 'Today', overdue }
  if (diffDays === 1) return { text: 'Tomorrow', overdue }
  if (diffDays === -1) return { text: 'Yesterday', overdue }
  if (diffDays > 1) return { text: `In ${diffDays} days`, overdue }
  return { text: `${Math.abs(diffDays)} days ago`, overdue }
}

const OVERDUE_STATUSES = ['pending', 'in_progress']

const PAGE_SIZE = 25

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tasks, setTasks] = useState<any[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState<number | null>(null)
  const [meetingsMap, setMeetingsMap] = useState<Record<number, Meeting>>({})
  const [showBookMeeting, setShowBookMeeting] = useState(false)
  const [rescheduleTarget, setRescheduleTarget] = useState<Meeting | null>(null)
  const [bookTaskCtx, setBookTaskCtx] = useState<{ contact: { id: number; name?: string; email?: string }; taskId: number } | null>(null)
  const [followUpBusy, setFollowUpBusy] = useState<number | null>(null)
  const [pendingCancelMeetingId, setPendingCancelMeetingId] = useState<number | null>(null)
  const [cancellingMeeting, setCancellingMeeting] = useState(false)

  // The URL is the source of truth for the filters, the sort column/direction
  // and the page, so reload / browser Back / a link pasted to a teammate all land on the
  // same view (WorkflowsPage precedent). Memoized off the *string* rather than
  // the URLSearchParams instance: the string is a primitive, so `listParams`
  // keeps its identity across unrelated re-renders and `fetchTasks` below fires
  // exactly once per real state change.
  const search = searchParams.toString()
  const listParams = useMemo(() => parseTaskListParams(new URLSearchParams(search)), [search])
  const { task_type: typeFilter, status: statusFilter, priority: priorityFilter, sort: sortField, dir: sortDir, page } = listParams
  const offset = page * PAGE_SIZE
  const filtersActive = hasActiveTaskFilters(listParams)

  // Every filter/sort/page control goes through here. `applyTaskListPatch` owns
  // the "changing a filter resets to page 1" rule and the "default values delete
  // their param" rule, so no caller can forget either.
  const patchParams = useCallback(
    (patch: Partial<TaskListParams>) => {
      setSearchParams(prev => applyTaskListPatch(prev, patch), { replace: false })
    },
    [setSearchParams],
  )

  const clearFilters = useCallback(() => {
    setSearchParams(prev => clearTaskListFilters(prev), { replace: false })
  }, [setSearchParams])

  // silent=true refreshes rows/stats/meetings without flashing the table
  // spinner (used after inline status changes, which already updated the row
  // optimistically).
  const fetchTasks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const params = buildTaskListQuery(listParams, PAGE_SIZE)
      const [tasksRes, statsRes, meetingsRes] = await Promise.all([
        tasksApi.getTasks(params),
        tasksApi.getStats(),
        meetingsApi.getMeetings().catch(() => ({ data: [] as Meeting[] })),
      ])
      setTasks(tasksRes.data)
      const totalHeader = parseInt((tasksRes.headers as any)?.['x-total-count'], 10)
      setTotal(Number.isFinite(totalHeader) ? totalHeader : null)
      setStats(statsRes.data)
      const map: Record<number, Meeting> = {}
      for (const m of meetingsRes.data) map[m.id] = m
      setMeetingsMap(map)
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [listParams])

  // Cancelling a meeting notifies every attendee and can't be undone, so it asks
  // through ConfirmDialog (§16 — native window.confirm is banned: it blocks the
  // main thread, can't be styled, and can't say *why* it matters). The dialog
  // stays open on failure so the user can retry without re-finding the row.
  const confirmCancelMeeting = async () => {
    if (pendingCancelMeetingId === null) return
    setCancellingMeeting(true)
    try {
      await meetingsApi.cancelMeeting(pendingCancelMeetingId)
      toast.success('Meeting cancelled')
      setPendingCancelMeetingId(null)
      await fetchTasks()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel meeting')
    } finally {
      setCancellingMeeting(false)
    }
  }

  const handleFollowUp = async (meetingId: number) => {
    setFollowUpBusy(meetingId)
    try {
      await meetingsApi.sendFollowUp(meetingId)
      toast.success('Follow-up reminder sent to the contact')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to send follow-up')
    } finally {
      setFollowUpBusy(null)
    }
  }

  useEffect(() => { fetchTasks() }, [fetchTasks])

  // Change a task's status from the inline Status dropdown. Optimistically
  // updates the row (and drops it when an active status filter no longer
  // matches) for instant feedback, then silently re-syncs the whole page from
  // the server — the backend may have cancelled/detached a linked meeting,
  // moved the due date, or changed stats, and the Actions/Due cells must
  // reflect that. Reverts on failure.
  const changeStatus = async (taskId: number, newStatus: string) => {
    const prevTasks = tasks
    setTasks(prev =>
      prev
        .map(t => (t.id === taskId ? { ...t, status: newStatus } : t))
        .filter(t => !statusFilter || t.status === statusFilter),
    )
    try {
      await tasksApi.updateTask(taskId, { status: newStatus })
      await fetchTasks(true)
    } catch (err) {
      console.error('Failed to update task status:', err)
      setTasks(prevTasks)
    }
  }

  // Wording for the live region above the table, kept next to the range string
  // the pagination line shows so the two can't drift apart.
  const rangeLabel = tasks.length > 0 ? `${offset + 1}–${offset + tasks.length}` : '0'
  const tableAnnouncement = loading
    // Existing rows dim rather than disappear during a refresh, so there is
    // something to report; the first load belongs to the spinner's own label.
    ? (tasks.length > 0 ? 'Updating tasks…' : '')
    : tasks.length === 0
      ? filtersActive
        ? 'No tasks match the current filters'
        : page > 0
          ? 'No tasks on this page'
          : 'No tasks yet'
      : total !== null
        ? `Showing tasks ${rangeLabel} of ${total}`
        : `Showing ${tasks.length} task${tasks.length === 1 ? '' : 's'}`

  return (
    <div className="space-y-5">
      {/* Header — §23 */}
      <div>
        <h1
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Tasks
        </h1>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Manage sales tasks generated by your workflows.
        </p>
      </div>

      {/* Hero stat block — title + decorative checklist motif + floating stat pills (§21 / §51) */}
      <section
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/50 p-5 sm:p-6',
          'bg-gradient-to-br from-white via-[#f6f7ff] to-[#eef0ff]',
          'dark:border-white/[0.08] dark:from-[#0f1011] dark:via-[#131421] dark:to-[#1a1b2e]',
        )}
      >
        {/* Decorative ascending-checklist motif — abstract stepped bars + check marks.
            currentColor indigo, theme-aware via parent text class. */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-2/3 max-w-[560px] text-[#7170ff] dark:text-[#7170ff]"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 560 240"
            preserveAspectRatio="xMaxYMid slice"
            className="absolute inset-0 h-full w-full opacity-85 dark:opacity-65"
          >
            <defs>
              <radialGradient id="tk-glow-1" cx="75%" cy="42%" r="55%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="tk-glow-2" cx="40%" cy="85%" r="50%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.08" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
              <pattern id="tk-dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.7" fill="currentColor" fillOpacity="0.22" />
              </pattern>
              <radialGradient id="tk-dot-fade" cx="70%" cy="42%" r="60%">
                <stop offset="0%" stopColor="white" stopOpacity="1" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </radialGradient>
              <mask id="tk-dot-mask">
                <rect width="100%" height="100%" fill="url(#tk-dot-fade)" />
              </mask>
              <linearGradient id="tk-card-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.08" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.03" />
              </linearGradient>
            </defs>

            {/* Glows + halftone */}
            <rect width="100%" height="100%" fill="url(#tk-glow-1)" />
            <rect width="100%" height="100%" fill="url(#tk-glow-2)" />
            <rect width="100%" height="100%" fill="url(#tk-dots)" mask="url(#tk-dot-mask)" />

            {/* Stacked task rows — three pill-shape outlines with leading checkbox squares.
                Ascending opacity front-to-back, slight horizontal offsets for depth. */}
            <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25">
              {/* Back row (dim) */}
              <rect x="380" y="48" width="170" height="28" rx="6" fill="url(#tk-card-fill)" strokeOpacity="0.28" />
              <rect x="392" y="56" width="12" height="12" rx="3" fill="none" strokeOpacity="0.28" />

              {/* Middle row */}
              <rect x="365" y="94" width="170" height="28" rx="6" fill="url(#tk-card-fill)" strokeOpacity="0.48" />
              <rect x="377" y="102" width="12" height="12" rx="3" fill="none" strokeOpacity="0.48" />
              {/* Checkmark inside middle checkbox */}
              <polyline
                points="379,108 382,111 387,105"
                fill="none"
                strokeOpacity="0.55"
                strokeWidth="1.5"
              />

              {/* Front row (highest contrast) */}
              <rect x="350" y="140" width="170" height="28" rx="6" fill="url(#tk-card-fill)" strokeOpacity="0.78" />
              <rect x="362" y="148" width="12" height="12" rx="3" fill="none" strokeOpacity="0.78" />
            </g>

            {/* Inner-row faux text lines */}
            <g stroke="currentColor" strokeLinecap="round" strokeWidth="1">
              <line x1="412" y1="62" x2="525" y2="62" strokeOpacity="0.20" />
              <line x1="397" y1="108" x2="510" y2="108" strokeOpacity="0.32" />
              <line x1="382" y1="154" x2="495" y2="154" strokeOpacity="0.45" />
            </g>

            {/* Status accent dots — trailing indicators for each row */}
            <g fill="currentColor">
              <circle cx="540" cy="62"  r="2.4" fillOpacity="0.30" />
              <circle cx="525" cy="108" r="2.4" fillOpacity="0.50" />
              <circle cx="510" cy="154" r="2.6" fillOpacity="0.75" />
            </g>

            {/* Baseline shadow grounds the stack */}
            <ellipse cx="430" cy="180" rx="120" ry="3" fill="currentColor" fillOpacity="0.06" />

            {/* Stray accent dots — punctuation */}
            <g fill="currentColor">
              <circle cx="295" cy="100" r="1.4" fillOpacity="0.35" />
              <circle cx="278" cy="140" r="1.2" fillOpacity="0.28" />
              <circle cx="552" cy="92"  r="1.4" fillOpacity="0.35" />
            </g>
          </svg>
        </div>

        {/* Title block */}
        <div className="relative z-10 max-w-md">
          <h2
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
          >
            Task overview
          </h2>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
          >
            Snapshot of pending, in-progress, and overdue tasks across your workflows.
          </p>
        </div>

        {/* Stat pills — float over the decoration */}
        <div className="relative z-10 mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total',        value: stats?.total ?? 0,        icon: ListChecks,        color: 'text-[#5e6ad2] dark:text-[#a5b4fc]',         bg: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]' },
            { label: 'Pending',      value: stats?.pending ?? 0,      icon: Clock,             color: 'text-amber-600 dark:text-amber-400',         bg: 'bg-amber-500/10 dark:bg-amber-500/[0.10]' },
            { label: 'In progress',  value: stats?.in_progress ?? 0,  icon: Hourglass,         color: 'text-[#5e6ad2] dark:text-[#a5b4fc]',         bg: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]' },
            { label: 'Overdue',      value: stats?.overdue ?? 0,      icon: WarningCircle,     color: stats?.overdue ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground dark:text-[#8a8f98]', bg: stats?.overdue ? 'bg-red-500/10 dark:bg-red-500/[0.10]' : 'bg-muted/60 dark:bg-white/[0.04]' },
            { label: 'Completed',    value: stats?.completed ?? 0,    icon: CheckSquareOffset, color: 'text-emerald-600 dark:text-emerald-400',     bg: 'bg-emerald-500/10 dark:bg-emerald-500/[0.10]' },
          ].map(stat => (
            <div
              key={stat.label}
              className={cn(
                'rounded-xl border bg-white px-4 py-5 transition-colors duration-150',
                'border-border/40 shadow-[0_1px_2px_rgba(15,16,17,0.04),0_4px_12px_rgba(15,16,17,0.04)]',
                'dark:bg-[#191a1b]/85 dark:backdrop-blur-sm dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.25)]',
              )}
            >
              <p
                className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
              >
                {stat.value}
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <div
                  className={cn('h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0', stat.bg)}
                  aria-hidden="true"
                >
                  <stat.icon className={cn('h-3.5 w-3.5', stat.color)} weight="regular" />
                </div>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Each filter writes the URL; the fetch follows from it. Page reset lives
            inside applyTaskListPatch rather than at each call site. */}
        <Select
          value={typeFilter}
          onChange={(v) => patchParams({ task_type: v })}
          ariaLabel="Task type"
          options={TASK_TYPE_OPTIONS}
          minWidth={170}
        />
        <Select
          value={statusFilter}
          onChange={(v) => patchParams({ status: v })}
          ariaLabel="Task status"
          options={STATUS_OPTIONS}
          minWidth={170}
        />
        <Select
          value={priorityFilter}
          onChange={(v) => patchParams({ priority: v })}
          ariaLabel="Task priority"
          options={PRIORITY_OPTIONS}
          minWidth={170}
        />
        {/* Only offered once something is narrowed — a dead "Clear" next to
            three "All …" dropdowns is noise. */}
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            icon={<X className="h-3.5 w-3.5" />}
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        )}
        <Button
          className="ml-auto"
          icon={<CalendarPlus className="h-4 w-4" />}
          onClick={() => setShowBookMeeting(true)}
        >
          Book Meeting
        </Button>
      </div>

      {/* One polite live region for the whole table. A <table> is not a live
          region, so changing a filter, flipping the sort, paging, or the silent
          re-sync after an inline status change all swapped the rows in complete
          silence for a screen-reader user. Deliberately quiet during the initial
          load: the spinner below carries its own role="status" label, and two
          regions announcing "loading tasks" is worse than one. */}
      <p className="sr-only" role="status" aria-live="polite">
        {tableAnnouncement}
      </p>

      {/* Table — §22 chrome */}
      <div className="rounded-lg border border-border/50 overflow-hidden bg-card dark:bg-white/[0.02] dark:border-white/[0.08]">
        <div className="overflow-auto max-h-[calc(100vh-16rem)] min-h-[320px]">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr className="border-b border-border/50 dark:border-white/[0.06]">
                {(['Title','Type','Contact','Assignee','Campaign','Priority','Status','Due','Created','Actions'] as const).map(h => {
                  const field: TaskSortField | null =
                    h === 'Due' ? 'due_date' : h === 'Created' ? 'created_at' : null
                  const active = field !== null && sortField === field
                  // Each column says its directions in its own words — "earliest"
                  // reads wrong for a creation time, "newest" reads wrong for a
                  // deadline.
                  const dirWord = (d: SortDir) => field === 'due_date'
                    ? (d === 'asc' ? 'earliest first' : 'latest first')
                    : (d === 'asc' ? 'oldest first' : 'newest first')
                  return (
                    <th
                      key={h}
                      className="sticky top-0 z-10 border-b border-border/50 bg-card px-4 py-3.5 text-left align-middle text-muted-foreground uppercase dark:border-white/[0.06] dark:bg-[#0f1011] dark:text-[#8a8f98]"
                      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {field ? (
                        <button
                          type="button"
                          // Clicking the sorted column reverses it; clicking the
                          // other switches to it at its own natural direction,
                          // which applyTaskListPatch supplies for a bare `sort`.
                          onClick={() => patchParams(active
                            ? { dir: sortDir === 'asc' ? 'desc' : 'asc' }
                            : { sort: field })}
                          className={`inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground dark:hover:text-[#f7f8f8]${active ? ' text-foreground dark:text-[#f7f8f8]' : ''}`}
                          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                          aria-label={active
                            ? `Sorted by ${h.toLowerCase()}, ${dirWord(sortDir)}. Activate to reverse.`
                            : `Sort by ${h.toLowerCase()}, ${dirWord(DEFAULT_SORT_DIR[field])}`}
                          title={active
                            ? `Sorted by ${h.toLowerCase()} (${dirWord(sortDir)}). Click to reverse.`
                            : `Click to sort by ${h.toLowerCase()} (${dirWord(DEFAULT_SORT_DIR[field])}).`}
                        >
                          {h}
                          {/* The caret marks which column is doing the sorting —
                              showing one on both would claim two active sorts. */}
                          {active && (sortDir === 'asc'
                            ? <CaretUp className="h-3 w-3" weight="bold" />
                            : <CaretDown className="h-3 w-3" weight="bold" />)}
                        </button>
                      ) : h}
                    </th>
                  )
                })}
              </tr>
            </thead>
            {/* Re-sorts/filter changes refresh in place: existing rows stay
                visible (dimmed) while new data loads; the big spinner only
                shows when there are no rows to keep on screen. */}
            <tbody
              className={cn(
                'divide-y divide-border/50 dark:divide-white/[0.06] transition-opacity duration-150',
                loading && tasks.length > 0 && 'opacity-50 pointer-events-none',
              )}
            >
              {loading && tasks.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    {/* The only thing on screen during the first load, so it has to
                        announce itself — a bare spinning glyph is invisible to a
                        screen reader. */}
                    <CircleNotch
                      className="h-6 w-6 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc] mx-auto"
                      weight="bold"
                      role="status"
                      aria-label="Loading tasks"
                    />
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    {/* §24 — an empty screen is an invitation to act, and the
                        invitation depends on *why* it's empty. Filtered-empty used
                        to show "Tasks created by your workflows will appear here",
                        which hides the fix at the moment the user needs it: the
                        tasks exist, the filters are just excluding them. A page
                        past the end of the list is a third case — nothing is
                        filtered, the offset is simply stale. Both narrowed cases
                        recover through the same reset. */}
                    <div className="max-w-md mx-auto">
                      <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                        {filtersActive || page > 0 ? (
                          <FunnelSimple className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                        ) : (
                          <ClipboardText className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                        )}
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8] mb-2"
                        style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                      >
                        {filtersActive
                          ? 'No tasks match these filters'
                          : page > 0
                            ? 'Nothing on this page'
                            : 'No tasks found'}
                      </h3>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed mb-6"
                        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                      >
                        {filtersActive
                          ? 'Try widening the type, status, or priority filter — or clear all three to see every task.'
                          : page > 0
                            ? 'This page is past the end of the list. The earlier pages still have tasks.'
                            : 'Tasks created by your workflows will appear here.'}
                      </p>
                      {filtersActive || page > 0 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={filtersActive
                            ? <X className="h-3.5 w-3.5" />
                            : <CaretLeft className="h-3.5 w-3.5" />}
                          onClick={clearFilters}
                        >
                          {filtersActive ? 'Clear filters' : 'Back to first page'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          icon={<CalendarPlus className="h-4 w-4" />}
                          onClick={() => setShowBookMeeting(true)}
                        >
                          Book Meeting
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : tasks.map(task => {
                const TypeIcon = getTypeIcon(task.task_type)
                const due = relativeDate(task.due_date)
                // Only open tasks count as overdue — mirrors /tasks/stats, so the
                // red rows always match the Overdue card (21839).
                const isOverdue = due.overdue && OVERDUE_STATUSES.includes(task.status)
                const rowMeeting = task.meeting_id ? meetingsMap[task.meeting_id] : undefined
                const providerLabel = rowMeeting ? meetingProviderLabel(rowMeeting.provider) : ''
                const priorityLabel = taskPriorityLabel(task.priority)
                // A Book Meeting task cancelled before any meeting was booked is
                // terminal: status is locked and no Book Meeting action is offered.
                const isMeetingTask = task.task_type === 'book_meeting' || task.task_type === 'meeting'
                const isTerminalCancelled = isMeetingTask && task.status === 'cancelled' && !task.meeting_id
                return (
                  <tr
                    key={task.id}
                    className="group transition-colors duration-150 hover:bg-muted/40 dark:hover:bg-white/[0.03]"
                  >
                    <td className="px-4 py-3.5">
                      {/* Hovering the title shows the task's description (21840) */}
                      <Tooltip
                        label={task.description?.trim() || task.title}
                        className="max-w-[320px] whitespace-normal leading-snug"
                      >
                        <span
                          className="block max-w-[280px] line-clamp-2 text-foreground dark:text-[#f7f8f8] cursor-default"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.45 }}
                          tabIndex={0}
                        >
                          {task.title}
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          <TypeIcon className="h-3.5 w-3.5 flex-shrink-0" weight="regular" />
                          {taskTypeLabel(task.task_type)}
                        </span>
                        {rowMeeting && rowMeeting.status !== 'cancelled' && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md border',
                              'border-border/50 bg-muted/40 text-foreground/80',
                              'dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-[#d0d6e0]',
                            )}
                            style={{ fontSize: '10.5px', fontWeight: 560, letterSpacing: '0.01em' }}
                            // Label, not the enum: an unnamed provider degrades to
                            // "Scheduled" rather than printing `google_meet`.
                            title={providerLabel ? `Meeting via ${providerLabel}` : 'Meeting scheduled'}
                          >
                            <span className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', PROVIDER_DOT[rowMeeting.provider] || 'bg-muted-foreground/50')} />
                            {providerLabel || 'Scheduled'}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {task.contact_name ? (
                        <span
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {task.contact_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {task.assigned_to ? (
                        <span
                          className="inline-flex items-center gap-1.5 text-foreground dark:text-[#f7f8f8] max-w-[200px]"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                          title={task.assigned_to}
                        >
                          <UserCircle className="h-4 w-4 flex-shrink-0 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                          <span className="truncate">{task.assigned_to}</span>
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 text-muted-foreground/70 dark:text-[#6e7178] italic"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          <UserCircle className="h-4 w-4 flex-shrink-0 opacity-50" weight="regular" />
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {task.campaign_name ? (
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] truncate"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          {task.campaign_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {/* Real label, so no CSS `capitalize` is needed — and a
                          priority the backend adds later reads as "Very high"
                          instead of `very_high` (which `capitalize` would have
                          rendered as "Very_high"). */}
                      {priorityLabel ? (
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded-full border',
                            PRIORITY_CLS[task.priority] || PRIORITY_CLS.medium,
                          )}
                          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                        >
                          {priorityLabel}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {isTerminalCancelled ? (
                        <Tooltip label="Cancelled before a meeting was booked — this task can't be re-activated. Create a new task instead.">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full border cursor-default bg-muted/60 text-muted-foreground border-border/60 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]"
                            style={{ fontSize: '11px', fontWeight: 560, letterSpacing: '-0.05px' }}
                            tabIndex={0}
                          >
                            Cancelled
                          </span>
                        </Tooltip>
                      ) : (
                        <Select
                          value={task.status}
                          onChange={(v) => changeStatus(task.id, v)}
                          // Row-specific list: a status we don't know is appended
                          // (humanized) so the trigger shows the task's real state
                          // instead of falling back to the "Select…" placeholder.
                          options={statusOptionsFor(task.status)}
                          ariaLabel={`Status for ${task.title}, currently ${taskStatusLabel(task.status) || 'unset'}`}
                          minWidth={140}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {/* Urgency-tiered due state (§31 chip idiom): red chip when
                          overdue, amber when due today, quiet text otherwise.
                          Hover shows the exact due date/time. */}
                      {(() => {
                        const exactDue = task.due_date
                          ? `Due ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(task.due_date))}`
                          : 'No due date'
                        const isDueToday = !isOverdue && due.text === 'Today' && OVERDUE_STATUSES.includes(task.status)
                        if (isOverdue) {
                          return (
                            <Tooltip label={exactDue}>
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border whitespace-nowrap cursor-default bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25"
                                style={{ fontSize: '11px', fontWeight: 560, letterSpacing: '-0.05px' }}
                                tabIndex={0}
                              >
                                <WarningCircle className="h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                                Overdue{due.text !== 'Today' ? ` · ${due.text.toLowerCase()}` : ''}
                              </span>
                            </Tooltip>
                          )
                        }
                        if (isDueToday) {
                          return (
                            <Tooltip label={exactDue}>
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border whitespace-nowrap cursor-default bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25"
                                style={{ fontSize: '11px', fontWeight: 560, letterSpacing: '-0.05px' }}
                                tabIndex={0}
                              >
                                <Clock className="h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                                Due today
                              </span>
                            </Tooltip>
                          )
                        }
                        return (
                          <Tooltip label={exactDue}>
                            <span
                              className="inline-flex items-center whitespace-nowrap tabular-nums cursor-default text-muted-foreground dark:text-[#8a8f98]"
                              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                              tabIndex={0}
                            >
                              {due.text}
                            </span>
                          </Tooltip>
                        )
                      })()}
                    </td>
                    <td className="px-4 py-3.5">
                      {/* Quiet by design: creation time orders the list but is
                          rarely the thing being read, so it carries none of the
                          Due column's urgency chrome. Hover gives the exact
                          timestamp, same idiom as Due. */}
                      <Tooltip label={task.created_at
                        ? `Created ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(task.created_at))}`
                        : 'Creation time unknown'}>
                        <span
                          className="inline-flex items-center whitespace-nowrap tabular-nums cursor-default text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                          tabIndex={0}
                        >
                          {relativeDate(task.created_at ?? null).text}
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-3.5">
                      {(() => {
                        const meeting = task.meeting_id ? meetingsMap[task.meeting_id] : undefined
                        const isMeetingType = task.task_type === 'meeting' || task.task_type === 'book_meeting'
                        const active = meeting && meeting.status !== 'cancelled'
                        // A cancelled task offers no booking action — booking is
                        // for open tasks only.
                        if (isMeetingType && task.status === 'cancelled') {
                          return <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                        }
                        // Unbooked meeting task -> offer to book it for this contact.
                        if (isMeetingType && !active) {
                          return (
                            <Button
                              variant="outline" size="sm"
                              icon={<CalendarPlus className="h-3.5 w-3.5" />}
                              onClick={() => setBookTaskCtx({
                                contact: { id: task.contact_id, name: task.contact_name, email: task.contact_email },
                                taskId: task.id,
                              })}>
                              Book Meeting
                            </Button>
                          )
                        }
                        if (!active) {
                          return <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                        }
                        return (
                          <div className="flex items-center gap-1">
                            {meeting!.join_url && (
                              <a href={meeting!.join_url} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-[#5e6ad2]/10 text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.18] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/25 transition-colors"
                                style={{ fontSize: '12px', fontWeight: 510 }}
                                title="Join meeting" aria-label="Join meeting">
                                <ArrowSquareOut className="h-3.5 w-3.5" /> Join
                              </a>
                            )}
                            <Tooltip label="Send follow-up reminder">
                              <button
                                onClick={() => handleFollowUp(meeting!.id)}
                                disabled={followUpBusy === meeting!.id}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-[#5e6ad2] dark:hover:bg-white/[0.06] dark:hover:text-[#a5b4fc] disabled:opacity-40"
                                aria-label="Send follow-up reminder">
                                {/* aria-hidden: the button already carries the
                                    accessible name, and `disabled` announces the
                                    pending state. */}
                                {followUpBusy === meeting!.id
                                  ? <CircleNotch className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                                  : <PaperPlaneTilt className="h-3.5 w-3.5" aria-hidden="true" />}
                              </button>
                            </Tooltip>
                            <Tooltip label="Reschedule">
                              <button
                                onClick={() => setRescheduleTarget(meeting!)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground dark:hover:bg-white/[0.06] dark:hover:text-[#f7f8f8]"
                                aria-label="Reschedule meeting">
                                <ArrowsClockwise className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                            <Tooltip label="Cancel meeting">
                              <button
                                onClick={() => setPendingCancelMeetingId(meeting!.id)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:bg-red-500/[0.12] dark:hover:text-red-400"
                                aria-label="Cancel meeting">
                                <XCircle className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          </div>
                        )
                      })()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/50 dark:border-white/[0.06]">
          <p
            className="text-muted-foreground dark:text-[#8a8f98]"
            style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
          >
            Showing{' '}
            <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
              {rangeLabel}
            </span>
            {total !== null && (
              <>
                {' '}of{' '}
                <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
                  {total}
                </span>
              </>
            )}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => patchParams({ page: page - 1 })}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
              aria-label="Previous page"
            >
              <CaretLeft className="h-4 w-4" weight="bold" />
            </button>
            <button
              type="button"
              disabled={total !== null ? offset + PAGE_SIZE >= total : tasks.length < PAGE_SIZE}
              onClick={() => patchParams({ page: page + 1 })}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
              aria-label="Next page"
            >
              <CaretRight className="h-4 w-4" weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Book Meeting (create) */}
      <BookMeetingModal
        isOpen={showBookMeeting}
        onClose={() => setShowBookMeeting(false)}
        onBooked={() => { setShowBookMeeting(false); fetchTasks() }}
      />

      {/* Book a meeting for a specific 'book meeting' task (locked to its contact) */}
      <BookMeetingModal
        isOpen={!!bookTaskCtx}
        onClose={() => setBookTaskCtx(null)}
        contact={bookTaskCtx?.contact}
        taskId={bookTaskCtx?.taskId}
        onBooked={() => { setBookTaskCtx(null); fetchTasks() }}
      />

      {/* Reschedule an existing meeting */}
      <BookMeetingModal
        isOpen={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        rescheduleTarget={rescheduleTarget || undefined}
        onBooked={() => { setRescheduleTarget(null); fetchTasks() }}
      />

      {/* Cancel meeting — the consequence the old window.confirm named ("attendees
          will be notified") stays explicit, because that is the part the user
          can't take back. */}
      <ConfirmDialog
        open={pendingCancelMeetingId !== null}
        title="Cancel this meeting?"
        description="The event is removed from the calendar and every attendee is emailed to say it was cancelled. You can't undo this — you'd have to book a new meeting."
        confirmLabel="Cancel meeting"
        cancelLabel="Keep it"
        tone="destructive"
        loading={cancellingMeeting}
        onConfirm={confirmCancelMeeting}
        onCancel={() => !cancellingMeeting && setPendingCancelMeetingId(null)}
      />
    </div>
  )
}
